import React from 'react';
import { X } from 'lucide-react';
import type { BaseFieldProps } from '../../../components/types';
import { FieldLabelWithHtml } from '../base-controls/FieldLabelWithHtml';
import { Button } from '../../../components/buttons/Button';
import { FieldErrorWithoutAria } from '../base-controls/FieldError';
import { TextInput } from '../base-controls/text-controls/TextInput';
import { baseControlStyles } from '../base-controls/util';

export interface TextArrayFieldProps extends BaseFieldProps {
	value: string[];
	onChange: (value: string[]) => void;
	onBlur?: () => void;
	isReadOnly?: boolean;
	isInvalid?: boolean;
	itemErrors?: (string | undefined)[];
	emptyLabel?: string;
	placeholder?: string;
}

export const TextArrayField = React.memo(function TextArrayFieldComponent(props: TextArrayFieldProps) {
	const { value = [], onChange, onBlur, isReadOnly, itemErrors, emptyLabel = 'Add an item', placeholder, hideLabel } = props;

	const handleAdd = () => {
		onChange([...value, '']);
	};

	const handleRemove = (index: number) => {
		onChange(value.filter((_, i) => i !== index));
	};

	const handleChange = (index: number, newValue: string) => {
		const updated = [...value];
		updated[index] = newValue;
		onChange(updated);
	};

	const isEmpty = value.length === 0;
	const { base } = baseControlStyles();

	return (
		<div className={base()}>
			{!hideLabel && <FieldLabelWithHtml className="mb-1" label={props.label} required={props.required} />}

			{isEmpty ? (
				<Button actionType="add" type="button" appearance="outline" onPress={handleAdd} className="w-fit" label={emptyLabel} />
			) : (
				<div className="flex flex-col gap-2">
					{value.map((item, index) => {
						const itemError = itemErrors?.[index];
						return (
							<div key={index} className="flex flex-col gap-1">
								<div className="flex items-center gap-2">
									<TextInput
										value={item}
										onChange={e => handleChange(index, e.target.value)}
										onBlur={onBlur}
										isDisabled={isReadOnly}
										placeholder={placeholder}
										isInvalid={!!itemError}
									/>
									{!isReadOnly && (
										<Button
											Icon={X}
											appearance="outline"
											intent="destructive"
											onClick={() => handleRemove(index)}
											aria-label="Remove item"
										/>
									)}
								</div>
								{itemError && <FieldErrorWithoutAria errorMessage={itemError} />}
							</div>
						);
					})}
					{!isReadOnly && (
						<Button actionType="add" type="button" appearance="outline" onPress={handleAdd} className="w-fit" label={emptyLabel} />
					)}
				</div>
			)}
			{props.error && <FieldErrorWithoutAria {...props.error} />}
		</div>
	);
});
